import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import useSWR from 'swr';
import { APIBatchOperation } from '../../../../../../typings/BatchOperation.interface';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { GlobalContext } from '../../contexts/GlobalContext';
import Badge from '../base/Badge';
import Button, { ClickableArea } from '../base/Button';
import { StyledCardSection } from '../base/Card';
import Chip from '../base/Chip';
import ChipOverflow from '../base/ChipOverflow';
import Icon from '../base/Icon';
import Loading from '../base/Loading';
import ProgressBar from '../base/ProgressBar';
import Text from '../base/Text';
import { useToasts } from '../Toast';
import { Div } from '../helpers/StyledUtils';
import BulkOperationBreakdown, { BulkOperationBreakdownCard, BulkOperationBreakdownRow } from './BulkOperationBreakdown';
import BulkOperationProgress from './BulkOperationProgress';
import { prettyPrintDate } from './utils';
import { useDatePreferences } from '../../hooks/useDatePreferences';
const PAGE_SIZE = 5;
type OperationKind = 'retry' | 'replay';
interface Props {
  filters: object;
  revalidateCounts: () => void;
}

/**
 * Target section of an expanded replay row. Resolves connection/source names
 * (falling back to the raw id when the resource no longer exists or hasn't
 * loaded yet) so the history reads like the rest of the dashboard.
 */
const ReplayTargetSummary: React.FC<{
  target: {
    source_id?: string;
    webhook_ids?: string[];
  };
}> = t0 => {
  const $ = _c(22);
  const {
    target
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  let t2;
  let target_ids;
  if ($[0] !== target.webhook_ids) {
    target_ids = target.webhook_ids ?? [];
    t1 = useSWR;
    t2 = target_ids.length > 0 ? APIMethodKeys.webhooks.list({
      id: target_ids,
      limit: 250
    }) : null;
    $[0] = target.webhook_ids;
    $[1] = t1;
    $[2] = t2;
    $[3] = target_ids;
  } else {
    t1 = $[1];
    t2 = $[2];
    target_ids = $[3];
  }
  let t3;
  if ($[4] !== HookdeckAPI.webhooks || $[5] !== target_ids) {
    t3 = () => HookdeckAPI.webhooks.list({
      id: target_ids,
      limit: 250
    });
    $[4] = HookdeckAPI.webhooks;
    $[5] = target_ids;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: webhooks_data
  } = t1(t2, t3);
  let t4;
  if ($[7] !== target.source_id) {
    t4 = target.source_id ? APIMethodKeys.sources.get(target.source_id) : null;
    $[7] = target.source_id;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== HookdeckAPI.sources || $[10] !== target.source_id) {
    t5 = () => HookdeckAPI.sources.get(target.source_id as string);
    $[9] = HookdeckAPI.sources;
    $[10] = target.source_id;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: target_source
  } = useSWR(t4, t5);
  const source_name = target_source?.name ?? target.source_id;
  let t6;
  if ($[12] !== source_name || $[13] !== target.source_id) {
    t6 = target.source_id && <BulkOperationBreakdownRow icon={<Icon muted={true} icon="source" left={true} />} label="Target Source"><Chip outline={true} mono={true} title={source_name} label={source_name} to={`/sources/${target.source_id}`} /></BulkOperationBreakdownRow>;
    $[12] = source_name;
    $[13] = target.source_id;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon muted={true} icon="connections" left={true} />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== target_ids || $[17] !== webhooks_data?.models) {
    t8 = <BulkOperationBreakdownRow icon={t7} label="Target Connections">{target_ids.length > 0 ? <ChipOverflow chips={target_ids.map(webhook_id => {
        const name = webhooks_data?.models.find(t9 => {
          const {
            id
          } = t9;
          return id === webhook_id;
        })?.full_name ?? webhook_id;
        return <Chip key={webhook_id} outline={true} mono={true} title={name} label={name} to={`/connections/${webhook_id}`} />;
      })} /> : <Text muted={true} size="s">All connections, resolved as the replay runs</Text>}</BulkOperationBreakdownRow>;
    $[16] = target_ids;
    $[17] = webhooks_data?.models;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t6 || $[20] !== t8) {
    t9 = <BulkOperationBreakdownCard title="Re-Ingestion Target">{t6}{t8}</BulkOperationBreakdownCard>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};

/**
 * Merged Ongoing/History list for the Requests bulk actions: interleaves
 * `requests.retry` and `requests.replay` operations chronologically so the
 * tabs show all bulk work regardless of the Retry/Replay mode toggle on the
 * Conditions tab. Each row is tagged with its kind. Bulk operations are
 * low-volume, so instead of cursor pagination across two sources the list
 * grows with a "Show more" that bumps the fetch limit on both.
 */
const BulkRequestOperationsHistory: React.FC<Props> = ({
  filters,
  revalidateCounts
}) => {
  const {
    timezone
  } = useDatePreferences();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const [open_id, setOpenedId] = useState<string>('');
  const [cancel_pending_id, setCancelPendingId] = useState<string>('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const list_filters = useMemo(() => ({
    ...filters,
    limit,
    dir: 'desc'
  }), [filters, limit]);
  const {
    data: retries,
    mutate: mutateRetries
  } = useSWR(APIMethodKeys.bulk.requests.list(list_filters), () => HookdeckAPI.bulk.requests.list(list_filters), {
    refreshInterval: 5000
  });
  const {
    data: replays,
    mutate: mutateReplays
  } = useSWR(APIMethodKeys.bulk.requestsReplay.list(list_filters), () => HookdeckAPI.bulk.requestsReplay.list(list_filters), {
    refreshInterval: 5000
  });
  const loaded = !!retries && !!replays;
  const operations = useMemo(() => {
    if (!loaded) return [];
    const tagged: {
      op: APIBatchOperation;
      kind: OperationKind;
    }[] = [...retries.models.map(op => ({
      op,
      kind: 'retry' as OperationKind
    })), ...replays.models.map(op_0 => ({
      op: op_0,
      kind: 'replay' as OperationKind
    }))];
    return tagged.sort((a, b) => a.op.created_at < b.op.created_at ? 1 : -1).slice(0, limit);
  }, [loaded, retries, replays, limit]);
  const has_more = loaded && (!!retries.pagination?.next || !!replays.pagination?.next || retries.models.length + replays.models.length > limit);
  useEffect(() => {
    revalidateCounts();
    !open_id && setOpenedId(operations[0]?.op.id ?? '');
  }, [operations, revalidateCounts]);
  const curried_toggle_opened = useCallback((id: string) => () => setOpenedId(open_id === id ? '' : id), [open_id]);
  if (!loaded) {
    return <StyledCardSection p={5}>
        <Loading />
      </StyledCardSection>;
  }
  if (operations.length === 0) {
    return <StyledCardSection p={4}>
        <Text muted>No bulk retries or replays.</Text>
      </StyledCardSection>;
  }
  return <>
      {operations.map(({
      op: bulk_operation,
      kind
    }) => {
      const target = bulk_operation.query?.target ?? {};
      const {
        target: _target,
        ...filter_only_query
      } = bulk_operation.query ?? {};
      const has_filters = Object.values(filter_only_query).some(value => Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined);
      return <StyledCardSection key={bulk_operation.id} muted={open_id === bulk_operation.id}>
            <ClickableArea onClick={curried_toggle_opened(bulk_operation.id)} w={100} flex={{
          justify: 'space-between',
          align: 'center'
        }} p={4}>
              <Div flex={{
            align: 'center'
          }}>
                <Icon icon={open_id === bulk_operation.id ? 'expand_less' : 'expand_more'} muted left={3} />
                <Text semi dark>
                  #{bulk_operation.number}
                </Text>
                <Badge subtle neutral m={{
              l: 2
            }}>
                  {kind === 'retry' ? 'Retry' : 'Replay'}
                </Badge>
                <Text semi dark m={{
              x: 6
            }}>
                  {prettyPrintDate(bulk_operation.created_at, timezone)}
                </Text>
                <Text muted mono>
                  {bulk_operation.id}
                </Text>
              </Div>
              {bulk_operation.cancelled_at ? <Badge subtle neutral>
                  Aborted
                </Badge> : bulk_operation.completed_at ? <Badge subtle success>
                  <NumberFormat renderText={v => v} displayType="text" value={bulk_operation.completed_count || 0} thousandSeparator={','} />
                </Badge> : bulk_operation.in_progress ? <Badge subtle warning>
                  <NumberFormat renderText={v_0 => v_0} displayType="text" value={bulk_operation.completed_count || 0} thousandSeparator={','} />
                  /
                  <NumberFormat renderText={v_1 => v_1} displayType="text" value={bulk_operation.estimated_count || 0} thousandSeparator={','} />
                </Badge> : <Badge subtle danger>
                  Failed
                </Badge>}
            </ClickableArea>
            {open_id === bulk_operation.id && (kind === 'retry' ? <Div p={{
          x: 4,
          b: 4
        }} m={{
          t: 0.25
        }}>
                  <BulkOperationBreakdown title="Filters" operation_type="retry" model="requests" query={bulk_operation.query} />
                  <BulkOperationProgress operation={bulk_operation} operation_type="retry" model="requests" onCancel={() => {
            mutateRetries();
            revalidateCounts();
          }} />
                </Div> : <Div p={{
          x: 4,
          b: 4
        }} m={{
          t: 0.25
        }}>
                  <Div flex={{
            direction: 'column',
            gap: 2
          }}>
                    {has_filters && <>
                        <BulkOperationBreakdown title="Filters" operation_type="retry" model="requests" query={filter_only_query} />
                        <Div flex={{
                justify: 'center'
              }}>
                          <Icon muted icon="arrow_downward" />
                        </Div>
                      </>}
                    <ReplayTargetSummary target={target} />
                  </Div>
                  {!bulk_operation.completed_at && !bulk_operation.cancelled_at && <Div m={{
            t: 6
          }}>
                      <Div flex={{
              justify: 'space-between'
            }}>
                        <Text size="s">Replay progress</Text>
                        <Text size="s">{Math.floor((bulk_operation.progress || 0) * 100)}%</Text>
                      </Div>
                      <ProgressBar m={{
              t: 3,
              b: 4
            }} value={Math.floor((bulk_operation.progress || 0) * 100)} danger={!!bulk_operation.cancelled_at} />
                    </Div>}
                  <Div flex={{
            justify: 'space-between',
            align: 'center'
          }} m={{
            t: 3
          }}>
                    <Text size="s" bold>
                      <NumberFormat renderText={v_2 => v_2} displayType="text" value={bulk_operation.completed_count || 0} thousandSeparator={','} />{' '}
                      request{bulk_operation.completed_count === 1 ? '' : 's'} replayed
                    </Text>
                    {bulk_operation.in_progress && <Button outline icon="block" disabled={cancel_pending_id === bulk_operation.id} onClick={async () => {
              setCancelPendingId(bulk_operation.id);
              try {
                await HookdeckAPI.bulk.requestsReplay.cancel(bulk_operation.id);
                addToast('success', `Aborted bulk replay #${bulk_operation.number}`);
                mutateReplays();
                revalidateCounts();
              } catch {
                addToast('error', `Failed to abort bulk replay #${bulk_operation.number}`);
              } finally {
                setCancelPendingId('');
              }
            }}>
                        Abort bulk replay
                      </Button>}
                  </Div>
                </Div>)}
          </StyledCardSection>;
    })}
      {has_more && <StyledCardSection flex={{
      justify: 'center'
    }} p={{
      x: 4,
      y: 3
    }}>
          <Button outline icon="expand_more" onClick={() => setLimit(limit + PAGE_SIZE)}>
            Show more
          </Button>
        </StyledCardSection>}
    </>;
};
export default BulkRequestOperationsHistory;